import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { px } from '@/utils/hubScale'

/**
 * The app's accent orange — the same one the primary CTAs use ("Nouvelle
 * demande", "Ouvrir le projet") and the one `BreadcrumbHeader` already carries
 * as `amber`. Deliberately NOT the red of `documentLedger.styles`' BackLink:
 * that belongs to the cool-slate documents handoff (#377) and reads as a
 * warning everywhere else.
 */
const ACCENT = '#f97316'
const ACCENT_HOVER = '#b45309'

/**
 * `&&` — doubled on purpose, do not simplify to `&`.
 *
 * Ant Design wraps the app in `.css-dev-only-do-not-override-<hash>` and styles
 * anchors through it: `.css-…-<hash> a { color: #1677ff }` is specificity
 * (0,1,1), which beats a single styled-components class (0,1,0). A plain `&`
 * loses that fight and the link renders antd blue — every other declaration
 * here still applies, so it fails as *only* a wrong colour, which is easy to
 * miss in review. Doubling the class to (0,2,0) wins without an `!important`.
 */
const Root = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    gap: ${px(6)};
    margin-bottom: ${px(12)};
    font-size: ${px(13)};
    font-weight: 700;
    color: ${ACCENT};
    text-decoration: none;
  }
  &&:hover {
    color: ${ACCENT_HOVER};
  }
`

/** Inherits the link's colour through `currentColor`. */
const Chevron = styled.svg`
  flex: none;
`

export type BackToListLinkProps = {
  /** Where the list lives. */
  to: string
  /** What the reader is going back to, e.g. "Retour projets". */
  label: string
  'data-cy'?: string
}

/**
 * One link out of a detail page, back to the list it was reached from.
 *
 * Replaces the two-item breadcrumbs that used to head these pages ("Mes
 * projets › <client>", "Études urbaines / <ref · client>"). Each trail carried
 * one real link and one label repeating the title rendered directly underneath
 * it, so the separator and the second crumb cost a row to say nothing — a
 * reader on a detail page wants the way back, not a map of where they are.
 *
 * Shared rather than per-feature so every escape hatch in the app is the same
 * control, which is what #590 asked for ("uniformiser et mettre en évidence").
 */
export const BackToListLink: React.FC<BackToListLinkProps> = ({
  to,
  label,
  'data-cy': dataCy,
}) => (
  <Root to={to} data-cy={dataCy}>
    <Chevron
      viewBox="0 0 24 24"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m15 18-6-6 6-6" />
    </Chevron>
    {label}
  </Root>
)
